import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/LangContext";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const { t } = useLang();
  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section id="top" ref={ref} className="relative min-h-screen pt-28 pb-16 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-end min-h-[calc(100vh-7rem)]">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center gap-3 mb-8 text-xs tracking-[0.25em] uppercase text-muted-foreground"
          >
            <span className="h-px w-10 bg-accent" />
            {t.hero.eyebrow}
          </motion.div>

          <h1 className="font-serif leading-[0.86] tracking-tight text-foreground text-6xl sm:text-7xl lg:text-8xl">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                >
                  {i === 2 ? <em className="italic text-accent">{l}</em> : l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 max-w-xl text-lg leading-relaxed text-foreground/75"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-book-now"
              href="https://pulisci-facile.emergent.host"
              className="group inline-flex items-center gap-3 bg-foreground text-background pl-7 pr-5 py-4 text-sm tracking-wide hover:bg-accent transition-colors duration-300"
            >
              {t.nav.book}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[3/4] overflow-hidden"
          >
            <motion.img
              src="https://images.pexels.com/photos/19473771/pexels-photo-19473771.jpeg"
              alt="Cozy immaculate living space"
              style={{ y, scale }}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
          </motion.div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 mt-6 text-xs tracking-[0.25em] uppercase text-muted-foreground">
        {t.hero.scroll}
      </div>
    </section>
  );
}
